import { useRef, useCallback } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import { usePostHog } from "@posthog/react";
import BuiltForCarousel from "@/components/BuiltForCarousel";
import { StatCard } from "@/components/StatCard";
import { AvailabilityPill } from "@/components/AvailabilityPill";

const floatKeyframes = `@keyframes hero-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }`;

function renderWords(text: string, highlightPhrase?: string) {
  const words = text.split(" ");
  let highlightStart = -1;
  if (highlightPhrase) {
    const idx = text.indexOf(highlightPhrase);
    if (idx >= 0) {
      highlightStart = text.slice(0, idx).split(" ").filter(Boolean).length;
    }
  }
  return words.map((word, i) => {
    const isHighlight = highlightStart >= 0 && i >= highlightStart;
    return (
      <span key={i} className={`inline-block mr-[0.1em] ${isHighlight ? "text-gradient" : ""}`}>
        {word}
      </span>
    );
  });
}

const HeroSection = () => {
  const prefersReduced = useReducedMotion();
  const t = useTranslation();
  const h = t.aiTeams.hero;
  const posthog = usePostHog();

  const heroRef = useRef<HTMLDivElement>(null);
  const spotlightRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLAnchorElement>(null);
  const ctaContainerRef = useRef<HTMLDivElement>(null);

  const { scrollY } = useScroll();
  const scrollOpacity = useTransform(scrollY, [0, 60], [1, 0]);

  const isFinePointer = typeof window !== "undefined" && window.matchMedia("(pointer: fine)").matches;

  const anim = {
    initial: prefersReduced ? { opacity: 0 } : { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const },
  };

  const handleHeroMove = useCallback((e: React.MouseEvent) => {
    if (prefersReduced || !isFinePointer) return;
    if (spotlightRef.current) {
      const rect = heroRef.current?.getBoundingClientRect();
      if (!rect) return;
      spotlightRef.current.style.background = `radial-gradient(600px circle at ${e.clientX - rect.left}px ${e.clientY - rect.top}px, hsl(166 84% 46% / 0.04), transparent 60%)`;
    }
    if (ctaRef.current && ctaContainerRef.current) {
      const r = ctaContainerRef.current.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = (e.clientX - cx) * 0.08;
      const dy = (e.clientY - cy) * 0.08;
      const max = 8;
      ctaRef.current.style.transform = `translate(${Math.max(-max, Math.min(max, dx))}px, ${Math.max(-max, Math.min(max, dy))}px)`;
    }
  }, [prefersReduced, isFinePointer]);

  const handleHeroLeave = useCallback(() => {
    if (spotlightRef.current) spotlightRef.current.style.background = "none";
    if (ctaRef.current) ctaRef.current.style.transform = "translate(0,0)";
  }, []);

  const headingFull = h.headingBefore2
    ? `${h.headingBefore} ${h.headingBefore2} ${h.headingHighlight}`
    : `${h.headingBefore} ${h.headingHighlight}`;

  return (
    <section
      ref={heroRef}
      onMouseMove={handleHeroMove}
      onMouseLeave={handleHeroLeave}
      className="relative flex flex-col min-h-screen pt-16"
    >
      {!prefersReduced && <style>{floatKeyframes}</style>}

      {/* Cursor spotlight */}
      <div ref={spotlightRef} className="absolute inset-0 pointer-events-none z-[1]" />

      {/* Noise texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Subtle grid background */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: "linear-gradient(hsl(166 84% 46%) 1px, transparent 1px), linear-gradient(90deg, hsl(166 84% 46%) 1px, transparent 1px)",
        backgroundSize: "60px 60px",
      }} />

      {/* Glow orb */}
      <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-primary/5 blur-[120px]" />

      {/* Availability pill - top right */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="hidden lg:block absolute top-28 right-[8%] z-20"
      >
        <AvailabilityPill text={h.availability} />
      </motion.div>

      {/* Main content - split layout */}
      <div className="flex-1 flex items-center">
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid lg:grid-cols-[55%_45%] gap-8 lg:gap-4 items-center">
            {/* Left column - text */}
            <div>
              <motion.p
                initial={anim.initial}
                animate={anim.animate}
                transition={{ delay: 0.2, ...anim.transition }}
                className="flex items-center gap-3 text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-8"
              >
                <span className="w-8 h-px bg-primary animate-pulse-glow" />
                {h.tagline}
              </motion.p>

              <motion.h1
                initial={prefersReduced ? { opacity: 0 } : { opacity: 0, y: 30 }}
                animate={anim.animate}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-display leading-[1.1] tracking-tight mb-8"
              >
                {h.headingBefore2 ? (
                  <>
                    <span className="lg:block lg:whitespace-nowrap">{renderWords(h.headingBefore)}</span>
                    <span className="lg:block">{renderWords(h.headingBefore2)}</span>
                    <br className="lg:hidden" />
                    {renderWords(h.headingHighlight, h.headingHighlight)}
                  </>
                ) : (
                  renderWords(headingFull, h.headingHighlight)
                )}
              </motion.h1>

              <motion.p
                initial={anim.initial}
                animate={anim.animate}
                transition={{ delay: 0.5, ...anim.transition }}
                className="text-lg font-body text-muted-foreground leading-relaxed max-w-xl mb-10"
              >
                {h.sub.split('\n').map((part, i) => (
                  <span key={i}>
                    {i > 0 && <br className="hidden sm:block" />}
                    {part}
                  </span>
                ))}
              </motion.p>

              <motion.div
                ref={ctaContainerRef}
                initial={anim.initial}
                animate={anim.animate}
                transition={{ delay: 0.6, ...anim.transition }}
                className="flex flex-wrap items-center gap-4"
              >
                <a
                  ref={ctaRef}
                  href="#book"
                  onClick={() => posthog?.capture("hero_cta_clicked", { cta: "primary" })}
                  className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-body font-semibold text-sm hover:bg-primary/90 transition-all glow"
                  style={{ willChange: "transform" }}
                >
                  <span className="italic">{h.ctaPrimary}</span>
                  <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                </a>
                <a
                  href="#services"
                  onClick={() => posthog?.capture("hero_cta_clicked", { cta: "secondary" })}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-body font-medium text-foreground border border-border hover:border-primary/40 transition-colors"
                >
                  {h.ctaSecondary}
                </a>
              </motion.div>
            </div>

            {/* Right column - asymmetric stat cards */}
            <div className="relative hidden lg:block h-[420px]">
              {[
                { pos: "absolute top-0 right-0 w-[260px]", delay: 0.8, floatDelay: "0s" },
                { pos: "absolute top-[140px] left-[10px] w-[220px]", delay: 1.0, floatDelay: "1.3s" },
                { pos: "absolute bottom-[20px] right-[40px] w-[240px]", delay: 1.2, floatDelay: "2.6s" },
              ].map((card, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: card.delay, duration: 0.5 }}
                  className={card.pos}
                  style={!prefersReduced ? { animation: `hero-float 4s ease-in-out ${card.floatDelay} infinite` } : undefined}
                >
                  <StatCard
                    value={h.statsPills[i].value}
                    suffix={h.statsPills[i].suffix}
                    label={h.statsPills[i].label}
                    valueClassName="text-gradient"
                  />
                </motion.div>
              ))}
            </div>

            {/* Mobile stat cards - vertical stack */}
            <div className="flex flex-col gap-3 lg:hidden">
              {h.statsPills.map((pill, i) => (
                <motion.div
                  key={pill.label}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + i * 0.1, duration: 0.4 }}
                >
                  <StatCard
                    value={pill.value}
                    suffix={pill.suffix}
                    label={pill.label}
                    valueClassName="text-gradient"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll affordance */}
      <motion.div
        style={{ opacity: prefersReduced ? 1 : scrollOpacity }}
        className="flex flex-col items-center pb-8 gap-1"
      >
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{t.scrollLabel}</span>
        <motion.div
          animate={prefersReduced ? {} : { y: [0, 4, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown size={14} className="text-muted-foreground" />
        </motion.div>
      </motion.div>

      {/* Built for section */}
      <BuiltForCarousel eyebrow={h.builtFor} items={h.trustedBy} />
    </section>
  );
};

export default HeroSection;
